import { useState, useEffect } from 'react';
import { listUsers, setUserRole, type User } from '@/shared/services/usersService';
import styles from './UsersPage.module.css';

type UserRole = User['role'];

const ROLE_LABELS: Record<UserRole, string> = {
  user: 'Usuario',
  courier: 'Repartidor',
  restaurant: 'Restaurante (App)',
  restaurant_admin: 'Restaurante Admin',
  admin: 'Administrador',
};

const ROLE_OPTIONS: UserRole[] = ['user', 'admin'];

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const usersList = await listUsers();
      setUsers(usersList);
    } catch (err) {
      let errorMessage = 'Error al cargar usuarios';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string }; status?: number } };
        if (axiosError.response?.status === 404) {
          errorMessage = 'El endpoint de usuarios no está disponible. Verifica que el backend esté desplegado.';
        } else if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
          errorMessage = 'No tienes permisos para ver usuarios.';
        } else {
          errorMessage = axiosError.response?.data?.message || axiosError.response?.data?.error || errorMessage;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (uid: string, newRole: UserRole) => {
    try {
      setChangingRole(uid);
      setError('');
      setSuccessMessage('');

      await setUserRole(uid, newRole);

      // Actualizar el usuario en la lista local
      setUsers((prevUsers) =>
        prevUsers.map((user) => (user.uid === uid ? { ...user, role: newRole } : user))
      );

      setSuccessMessage(`Rol cambiado a "${ROLE_LABELS[newRole]}" exitosamente.`);

      // Limpiar mensaje después de 3 segundos
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cambiar el rol';
      setError(errorMessage);
    } finally {
      setChangingRole(null);
    }
  };

  const formatDate = (timestamp?: { seconds: number; nanoseconds: number }) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando usuarios...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Gestión de Usuarios</h1>
        <p className={styles.subtitle}>Administra los roles y permisos de los usuarios del sistema</p>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {successMessage && <div className={styles.success}>{successMessage}</div>}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Email</th>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Rol Actual</th>
              <th>Estado</th>
              <th>Fecha Creación</th>
              <th>Cambiar Rol</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  No hay usuarios registrados
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.uid}>
                  <td>{user.email}</td>
                  <td>{user.fullName || 'N/A'}</td>
                  <td>{user.phone || 'N/A'}</td>
                  <td>
                    <span className={`${styles.roleBadge} ${styles[`role${user.role}`]}`}>
                      {ROLE_LABELS[user.role]}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[`status${user.status}`]}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.uid, e.target.value as UserRole)}
                      disabled={changingRole === user.uid}
                      className={styles.roleSelect}
                    >
                      {ROLE_OPTIONS.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </option>
                      ))}
                    </select>
                    {changingRole === user.uid && (
                      <span className={styles.changing}>Cambiando...</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <p className={styles.count}>Total: {users.length} usuarios</p>
        <button onClick={loadUsers} className={styles.refreshButton} disabled={loading}>
          {loading ? 'Actualizando...' : 'Actualizar Lista'}
        </button>
      </div>
    </div>
  );
}

